import { DateTime } from 'luxon';

/**
 * Parse a UTC ISO string from the API into a Luxon DateTime in the restaurant's
 * local timezone (Settings.defaultZone, set globally in BootstrapGate.tsx).
 */
export function fromApi(isoString: string): DateTime {
  return DateTime.fromISO(isoString, { zone: 'utc' }).toLocal();
}

/**
 * Parse a YYYY-MM-DD date string as a start-of-day instant in restaurant local time.
 * Use for calendar-day values that have no time component.
 */
export function fromDateString(dateStr: string): DateTime {
  return DateTime.fromISO(dateStr); // no offset → interpreted in Settings.defaultZone
}

/** Today's date as YYYY-MM-DD in the restaurant's local timezone. */
export function todayString(): string {
  return DateTime.now().toISODate()!;
}

/** YYYY-MM-DD that is `days` calendar days after today, in the restaurant's
 *  local timezone. Used as the upper bound for the booking window (advance
 *  window) so the date picker can't reach beyond what the server accepts. */
export function dateStringDaysAhead(days: number): string {
  return DateTime.now().plus({ days }).toISODate()!;
}

/** End of the current local day (23:59:59.999) as a UTC ISO instant with a `Z`
 *  suffix (via JS Date, so it satisfies zod's `.datetime()`). Used to pause new
 *  bookings for the rest of today. */
export function endOfTodayIso(): string {
  return DateTime.now().endOf('day').toJSDate().toISOString();
}

/** Whether a "YYYY-MM-DD" + "HH:mm" wall-clock pair is at or before now, judged
 *  in the restaurant's local timezone (Settings.defaultZone). Use for past-time
 *  guards — a native `new Date(\`${date}T${time}\`)` would parse in the staff
 *  browser's OS zone instead, mis-flagging bookings when staff and restaurant
 *  are in different zones. */
export function isLocalDateTimePast(dateStr: string, timeStr: string): boolean {
  const dt = DateTime.fromISO(`${dateStr}T${timeStr}`); // Settings.defaultZone
  return dt.isValid && dt <= DateTime.now();
}

/** Whether a `pausedUntil` ISO instant (or null) is still in the future, i.e. a
 *  pause is currently in effect. */
export function isPauseActive(pausedUntilIso: string | null): boolean {
  if (!pausedUntilIso) return false;
  return DateTime.fromISO(pausedUntilIso) > DateTime.now();
}

/** Format a UTC API timestamp as a full local date label: "Thursday, May 10, 2026". */
export function formatDate(isoString: string): string {
  return fromApi(isoString).toFormat('cccc, LLLL d, yyyy');
}

/** Format a UTC API timestamp as local time: "18:00". */
export function formatTime(isoString: string): string {
  return fromApi(isoString).toFormat('HH:mm');
}

/** Compact local date, no year: "Jun 10". For tight spots that pair with a time
 *  (matches the month convention of formatDateTime). */
export function formatShortDate(isoString: string): string {
  return fromApi(isoString).toFormat('LLL d');
}

/** Format a UTC API timestamp as a short date + time label: "May 10, 6:00 PM". */
export function formatDateTime(isoString: string): string {
  return fromApi(isoString).toFormat('LLL d, h:mm a');
}

/** Short date + 24-hour time: "May 10, 18:00". Used where the day matters but
 *  the time should stay 24-hour (e.g. a scheduled order). */
export function formatDateTime24(isoString: string): string {
  return fromApi(isoString).toFormat('LLL d, HH:mm');
}

/** Format a YYYY-MM-DD string as a full date label: "Thursday, May 10, 2026". */
export function formatDateString(dateStr: string): string {
  return fromDateString(dateStr).toFormat('cccc, LLLL d, yyyy');
}

/** Day-of-month number from a UTC API timestamp: "12". */
export function formatDay(isoString: string): string {
  return fromApi(isoString).toFormat('d');
}

/** Three-letter month from a UTC API timestamp: "JUN". */
export function formatMonth(isoString: string): string {
  return fromApi(isoString).toFormat('LLL').toUpperCase();
}

/** Short weekday from a UTC API timestamp: "Mon". */
export function formatWeekday(isoString: string): string {
  return fromApi(isoString).toFormat('ccc');
}

/**
 * Compact, localized relative time for a UTC API timestamp, chat-app style:
 * "now" / "5m ago" / "2h ago" / "yesterday" / "3d ago", falling back to a short
 * date ("Jun 10") once it's a week or older. Pass the current UI locale (e.g.
 * from `useI18nStore`) so the wording matches - defaults to English.
 */
export function formatRelative(isoString: string, locale = 'en'): string {
  const then = fromApi(isoString);
  const now = DateTime.now();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'narrow' });

  const minutes = Math.round(now.diff(then, 'minutes').minutes);
  if (minutes < 1) return rtf.format(0, 'second');
  if (minutes < 60) return rtf.format(-minutes, 'minute');

  const hours = Math.round(now.diff(then, 'hours').hours);
  if (hours < 24) return rtf.format(-hours, 'hour');

  // Calendar-day difference (not a raw 24h diff) so a message from earlier
  // yesterday reads "yesterday" rather than "23h ago".
  const dayDiff = Math.round(now.startOf('day').diff(then.startOf('day'), 'days').days);
  if (dayDiff < 7) return rtf.format(-dayDiff, 'day');

  return formatShortDate(isoString);
}
